import React from 'react'
import useField from 'components/Form/useField'
import styled, { css } from 'styled-components/macro'
import icon from 'styles/mixins/icon'
import { body2, caption } from 'styles/mixins/textstyles'

const RadioRoot = styled.div`
  padding: 0;
  position: relative;

  .radio-field {
    input {
      width: 2rem;
      height: 2rem;
      position: absolute;
      top: 1.5rem;
      left: 0;
      z-index: 1;
      opacity: 0;
      appearance: none;
      cursor: pointer;
    }
  
    label {
      ${props => body2(props.theme.colors.black)}
      
      margin-left: 2.8rem;
      padding-top: ${props => props.tight ? '.6rem' : '2.2rem'};
    }
  }
`

const RadioCircle = styled.span`
  ${icon()}
  
  font-size: 1.2rem;
  background: ${props => props.theme.colors.greyLighter};
  width: 2rem;
  height: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${props => props.theme.colors.white};
  border-radius: 100%;
  position: absolute;
  top: ${props => props.tight ? '.5rem' : '2.1rem'};
  left: 0;
  z-index: 0;

  ${props => props.checked && css`
    background: ${props => props.theme.colors.primaryBase};
  `}
`

const RadioIcon = styled.div`
  visibility: ${props => props.checked ? 'visible' : 'hidden'};
  background: ${props => props.theme.colors.white};
  width: 50%;
  height: 50%;
  border-radius: 100%;
`

const Radio = ({ checked, tight }) => (
  <RadioCircle checked={checked} tight={tight}><RadioIcon checked={checked} /></RadioCircle>
)

const Label = styled.label`
  ${props => caption(props.theme.colors.black)}
  display: block;
  text-align: left;
  color: ${props => props.theme.colors.greyBase};
  font-size: 1.3rem;
  padding-top: 1.4rem;
  padding-bottom: .2rem;

  ${props => props.focused && css`
    color: ${props => props.theme.colors.primaryBase};
  `}

  ${props => props.invalid && css`
    color: ${props => props.theme.colors.errorBase};
  `}

  ${props => props.disabled && css`
    color: ${props => props.theme.colors.greyLight} !important;
  `}
`

const radioChildrenMarginLeft = css`
  margin-left: 3.6rem;
`

const RadioChildren = styled.div`
  ${props => props.marginLeft && radioChildrenMarginLeft}
`

export const RadioGroup = props => {
  const { defaultValue, name, validate } = props
  const { value, setValue, error, id } = useField({ name, validate, defaultValue })

  const groupId = props.id || id

  const handleChange = event => {
    if (event.target.checked) {
      setValue(event.target.value)
      if (props.onChange) {
        event.persist()
        props.onChange(event)
      }
    }
  }

  if (typeof props.children !== 'function') {
    throw new Error('RadioGroup requires function children')
  }

  return (
    <>
      { error 
        ? <Label 
            id={`${id}-label`}
            disabled={props.disabled}
            htmlFor={id}
            invalid={true}
            focused={false}
          >
            { error }
          </Label>
        : '' 
      }
      { props.children({ name, selectedValue: value, error, handleChange, groupId }) }
    </>
  )
}

export const RadioElement = ({ selectedValue, handleChange, groupId, children, childrenMarginLeft = true, ...props }) => {
  const id = props.id || `${groupId}-${props.value}`
  const checked = selectedValue === props.value
  return (
    <RadioRoot tight={props.tight}>
      <span className="radio-field">
        <Label id={`${id}-label`} disabled={props.disabled} htmlFor={id} invalid={false} focused={false}>{props.label || '\u00A0'}</Label>
        <input type="radio" {...props} id={id} checked={checked} onChange={handleChange} />
      </span>
      <Radio checked={checked} tight={props.tight}/>
      {(typeof children === 'function') && (
        <RadioChildren marginLeft={childrenMarginLeft}>
          {children(checked)}
        </RadioChildren>
      )}
    </RadioRoot>
  )
}